'use strict';

const _ = require('lodash');
// Vue is a frontend package so must be included like this for compatibility with the frontend webpack in elsa
// otherwise simply doing `require('vue');` won't work in elsa
const Vue = require('vue/dist/vue');

const utils = require('../utils');
const fixedTree = require('./fixed-tree');
const weeklyVariables = require('./variables');
const envelope = require('./envelope');
const consts = require('../consts');

function create(budget) {
  return new Vue({
    data: {
      envelopes: _.map(utils.getNonVariableEnvelopes(budget), e => envelope.create(e, budget.lastUpdatedAt)),
      variableTransactions: utils.getEnvelope(budget, consts.VARIABLES_ENVELOPE_TYPE).actuals,
      variableIncomeTransactions: utils.getEnvelope(budget, consts.VARIABLE_INCOME_ENVELOPE_TYPE).actuals,
      excluded: budget.excluded,
      budgetDate: budget.budgetDate,
      lastUpdatedAt: budget.lastUpdatedAt,
      customerId: budget.customerId,
      created: budget.created,
      params: budget.params || {},
    },
    created() {
      if (this.hasCashflowBuilderRiseupGoal) {
        Vue.set(this.riseupGoalEnvelope, 'predictedAmount', this.computeRiseupGoal());
      }
    },
    computed: {
      isPredictedIncome() {
        return _.isNumber(this.variableIncomePredictionAmount);
      },
      incomeEnvelopes() {
        return _.filter(this.envelopes, e => !e.isExpense);
      },
      expenseEnvelopes() {
        return _.filter(this.envelopes, e => e.isExpense);
      },
      incomeEnvelopesBalanceSum() {
        return _.sumBy(this.incomeEnvelopes, e => e.balancedAmount);
      },
      incomeEnvelopesOriginalSum() {
        return _.sumBy(this.incomeEnvelopes, e => e.predictedAmount);
      },
      incomeEnvelopesActualSum() {
        return _.sumBy(this.incomeEnvelopes, e => e.actualsAmount);
      },
      expenseEnvelopesBalanceSum() {
        return _.sumBy(this.expenseEnvelopes, e => e.balancedAmount);
      },
      expenseEnvelopesOriginalSum() {
        return _.sumBy(this.expenseEnvelopes, e => e.predictedAmount);
      },
      expenseEnvelopesActualSum() {
        return _.sumBy(this.expenseEnvelopes, e => e.actualsAmount);
      },
      variableIncomeSum() { // kept for backward compatibility. To be removed
        return this.variableIncomeActualSum;
      },
      variableIncomeActualSum() {
        return utils.sumTransactions(this.variableIncomeTransactions);
      },
      variableIncomeOriginalAmount() {
        return this.isPredictedIncome ? -(this.adjustedIncomePredictionAmount + this.incomeEnvelopesOriginalSum) : 0;
      },
      adjustedIncomePredictionAmount() {
        if (this.variableIncomePredictionAmount) {
          return this.variableIncomePredictionAmount;
        }
        return null;
      },
      remainingIncomePrediction() {
        return Math.min(0, this.variableIncomeBalancedAmount - this.variableIncomeActualSum);
      },
      variableIncomeBalancedAmount() {
        if (this.variableIncomePredictionAmount) {
          return Math.min(this.variableIncomeActualSum, this.variableIncomePredictionAmount);
        }

        return this.variableIncomeActualSum;
      },
      variableOriginalAmount() {
        return -(this.variableIncomeOriginalAmount + this.incomeEnvelopesOriginalSum + this.expenseEnvelopesOriginalSum);
      },
      variableBalancedAmount() {
        return -(this.variableIncomeBalancedAmount + this.incomeEnvelopesBalanceSum + this.expenseEnvelopesBalanceSum);
      },
      variableActualSum() {
        return utils.sumTransactions(this.variableTransactions);
      },
      variableEnvelope() {
        return {
          id: `${this.budgetDate}#${consts.VARIABLE_ENVELOPE_ID}`,
          originalAmount: this.variableOriginalAmount,
          balancedAmount: this.variableBalancedAmount,
          actuals: _.cloneDeep(this.variableTransactions),
          type: consts.VARIABLES_ENVELOPE_TYPE,
        };
      },
      variableIncomeEnvelope() {
        return {
          id: `${this.budgetDate}#${consts.VARIABLE_INCOME_ENVELOPE_ID}`,
          originalAmount: this.variableIncomeOriginalAmount,
          balancedAmount: this.variableIncomeBalancedAmount,
          actuals: _.cloneDeep(this.variableIncomeTransactions),
          type: consts.VARIABLE_INCOME_ENVELOPE_TYPE,
          details: utils.getEnvelope(budget, consts.VARIABLE_INCOME_ENVELOPE_TYPE).details,
        };
      },
      incomeBalanceSum() {
        return this.incomeEnvelopesBalanceSum + this.variableIncomeBalancedAmount;
      },
      incomeActualSum() {
        return this.incomeEnvelopesActualSum + this.variableIncomeActualSum;
      },
      expenseBalanceSum() {
        return this.expenseEnvelopesBalanceSum + this.variableActualSum;
      },
      expenseActualSum() {
        return this.expenseEnvelopesActualSum + this.variableActualSum;
      },
      balance() {
        return this.incomeActualSum + this.expenseActualSum;
      },
      predictedBalance() {
        return (this.incomeBalanceSum + this.expenseBalanceSum) - this.riseupGoal;
      },
      variableIncomePredictionAmount() {
        return this.params.variableIncomePredictionAmount;
      },
      riseupGoalEnvelope() {
        return _.find(this.envelopes, e => e.type === consts.RISEUP_GOAL_ENVELOPE_TYPE);
      },
      riseupGoal() {
        return (this.riseupGoalEnvelope && this.riseupGoalEnvelope.balancedAmount) || 0;
      },
      hasCashflowBuilderRiseupGoal() {
        return this.riseupGoalEnvelope && this.riseupGoalEnvelope.details && this.riseupGoalEnvelope.details.averageMonthlySavings !== undefined;
      },
      fixedTree() {
        return fixedTree.create(this.envelopes);
      },
      weeklyVariables() {
        return weeklyVariables.create({
          budgetDate: this.budgetDate,
          lastUpdatedAt: this.lastUpdatedAt,
          variableBalancedAmount: this.variableBalancedAmount,
          variableTransactions: this.variableTransactions,
        });
      },
      allTransactions() {
        return [
          ...this.variableTransactions,
          ...this.variableIncomeTransactions,
          ..._.flatMap(this.envelopes, e => e.actuals),
          ...this.excluded,
        ];
      },
    },
    methods: {
      computeRiseupGoal() {
        const { averageMonthlySavings, riseupGoalInputAmount } = this.riseupGoalEnvelope.details;
        // Until we decide if the riseup goal is an envelope or not we need to subtract the riseup goal
        // from variables for cases where we put the cashflow through optimistic a few times.
        // If so, it makes the this.variableOriginalAmount include the riseupGoal and remodify itself in a loop.
        const variableOriginalAmount = this.variableOriginalAmount + this.riseupGoal;
        if (_.isNumber(riseupGoalInputAmount)) {
          return riseupGoalInputAmount;
        }
        if (variableOriginalAmount < 10) {
          return 0;
        }
        if (variableOriginalAmount < 1510) {
          return 10;
        }
        if (variableOriginalAmount > 0.5 * -this.incomeEnvelopesOriginalSum && -this.incomeEnvelopesOriginalSum > 21000) {
          return variableOriginalAmount - (0.5 * -this.incomeEnvelopesOriginalSum);
        }
        if (averageMonthlySavings < 10) {
          return 10;
        }
        if (variableOriginalAmount - averageMonthlySavings < 1500) {
          return variableOriginalAmount - 1500;
        }
        return averageMonthlySavings || 0;
      },
      toJson() {
        const envelopes = _.concat(
          _.map(this.envelopes, e => e.toJson()),
          [this.variableEnvelope],
          [this.variableIncomeEnvelope],
        );
        return {
          envelopes,
          excluded: _.cloneDeep(this.excluded),
          budgetDate: this.budgetDate,
          lastUpdatedAt: this.lastUpdatedAt,
          customerId: this.customerId,
          created: this.created,
          params: _.cloneDeep(this.params),
        };
      },
      getCashculations() {
        return {
          // balance
          riseupGoal: this.riseupGoal,
          balance: this.balance,
          predictedBalance: this.predictedBalance,
          isPredictedIncome: this.isPredictedIncome,

          // expense
          expenseActualSum: this.expenseActualSum,
          expenseBalanceSum: this.expenseBalanceSum,

          // variable expense
          variableActualSum: this.variableActualSum,
          variableBalancedAmount: this.variableBalancedAmount,

          // expense envelopes
          expenseEnvelopesActualSum: this.expenseEnvelopesActualSum,
          expenseEnvelopesBalanceSum: this.expenseEnvelopesBalanceSum,

          // fixed expense envelops
          fixedExpenseEnvelopesActualSum: this.fixedTree.fixedExpenseEnvelopesActualSum,
          fixedExpenseEnvelopesBalanceSum: this.fixedTree.fixedExpenseEnvelopesBalanceSum,
          fixedExpenseEnvelopesPredictedSum: this.fixedTree.fixedExpenseEnvelopesPredictedSum,

          // income
          incomeActualSum: this.incomeActualSum,
          incomeBalanceSum: this.incomeBalanceSum,

          // variable income
          variableIncomeActualSum: this.variableIncomeActualSum,
          variableIncomeBalanceSum: this.variableIncomeBalancedAmount,
          variableIncomePredictionAmount: this.variableIncomePredictionAmount,

          // income envelopes
          incomeEnvelopesActualSum: this.incomeEnvelopesActualSum,
          incomeEnvelopesBalanceSum: this.incomeEnvelopesBalanceSum,
          incomeEnvelopesPredictedSum: this.incomeEnvelopesOriginalSum,

          // fixed income envelopes
          fixedIncomeEnvelopesActualSum: this.fixedTree.fixedIncomeEnvelopesActualSum,
          fixedIncomeEnvelopesBalanceSum: this.fixedTree.fixedIncomeEnvelopesBalanceSum,
          fixedIncomeEnvelopesPredictedSum: this.fixedTree.fixedIncomeEnvelopesPredictedSum,

          weeklyVariablesCashculations: this.weeklyVariables.getCashculations(),
          envelopes: _.map(this.envelopes, e => e.cashculation),
        };
      },
      getEnvelopesByType(envelopeType) {
        return _.filter(this.envelopes, e => e.type === envelopeType);
      },
      setVariableIncomePredictionAmount(amount) {
        Vue.set(this.params, 'variableIncomePredictionAmount', amount);
      },
    },
  });
}

module.exports = {
  create,
};
